import type { RefObject } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { SwipeDirections } from 'containers/RowContainer/RowContainer.h'

const THROTTLE_SWIPE_IN_MILLIS = 30

type useSwiperBannerProps = {
  bannerRef: RefObject<HTMLDivElement | null>
  prevFn: () => void
  nextFn: () => void
}
export const useSwipeBanner = ({
  bannerRef,
  prevFn,
  nextFn,
}: useSwiperBannerProps) => {
  const [startTouch, setStartTouch] = useState(0)
  const [stopTouch, setStopTouch] = useState(0)
  const [startTouchY, setStartTouchY] = useState(0)

  const swipeDirection = useRef<SwipeDirections>(SwipeDirections.Horizontal)

  const setSwipeDirection = useCallback(
    ({
      positionX,
      positionY,
      startTouch,
      startTouchY,
    }: {
      positionX: number
      positionY: number
      startTouch: number
      startTouchY: number
    }): SwipeDirections => {
      const xMovement = Math.abs(positionX - startTouch)
      const yMovement = Math.abs(positionY - startTouchY)
      const direction =
        xMovement > yMovement
          ? SwipeDirections.Horizontal
          : SwipeDirections.Vertical

      swipeDirection.current = direction

      return direction
    },
    []
  )

  useEffect(() => {
    const swipeScroll = (): void => {
      if (
        startTouch > stopTouch &&
        startTouch - stopTouch > THROTTLE_SWIPE_IN_MILLIS
      ) {
        return nextFn()
      }
      if (
        startTouch < stopTouch &&
        stopTouch - startTouch > THROTTLE_SWIPE_IN_MILLIS
      ) {
        return prevFn()
      }
    }
    swipeScroll()
  }, [stopTouch])

  useEffect(() => {
    const touchstart = (e: TouchEvent): void => {
      setStartTouch(e.changedTouches[0].clientX)
      setStartTouchY(e.changedTouches[0].clientY)
    }

    const touchend = (e: TouchEvent): void => {
      if (swipeDirection.current === SwipeDirections.Horizontal) {
        setStopTouch(e.changedTouches[0].clientX)
      }
    }

    bannerRef.current?.addEventListener('touchstart', touchstart)
    bannerRef.current?.addEventListener('touchend', touchend)
    return () => {
      bannerRef.current?.removeEventListener('touchstart', touchstart)
      bannerRef.current?.removeEventListener('touchend', touchend)
    }
  }, [bannerRef.current])

  useEffect(() => {
    const touchmove = (e: TouchEvent): void => {
      const positionX = e.targetTouches[0].clientX
      const positionY = e.targetTouches[0].clientY

      const direction = setSwipeDirection({
        positionX,
        positionY,
        startTouch,
        startTouchY,
      })

      if (direction === SwipeDirections.Horizontal && e.cancelable) {
        e.preventDefault()
      }
    }

    bannerRef.current?.addEventListener('touchmove', touchmove)

    return () => {
      bannerRef.current?.removeEventListener('touchmove', touchmove)
    }
  }, [startTouch, startTouchY, bannerRef.current])
}
